import type { TierIIChemicalCandidate, TierIIProductReportingCandidate } from '../types/reporting.ts';

/** Coverage is a report destination, never a replacement for threshold assessment. */
export function chemicalReportingCoverage(candidate: TierIIChemicalCandidate) {
  const destinations = new Set(candidate.reportingAllocation.mixtureProductIDs);
  const eligible = candidate.exemptionCode
    ? []
    : candidate.products.filter((product) => !product.exemptionCode && product.concentration > 0);
  const covered = eligible.filter((product) => product.reportAsMixture && destinations.has(product.productID));
  const coveredIDs = new Set(covered.map((product) => product.productID));
  const remaining = eligible.filter((product) => !coveredIDs.has(product.productID));
  return { covered, remaining, fullyCovered: covered.length > 0 && remaining.length === 0 };
}

export function chemicalInventoryNeedsReview(candidate: TierIIChemicalCandidate): boolean {
  return (
    candidate.compoundMaxLbs == null ||
    candidate.compoundAvgLbs == null ||
    candidate.products.some(
      (product) =>
        !product.exemptionCode &&
        product.concentration > 0 &&
        (product.maxAmountLbs == null ||
          product.avgAmountLbs == null ||
          product.compoundMaxLbs == null ||
          product.compoundAvgLbs == null),
    )
  );
}

/**
 * An unanswered EHS qualifier only blocks reporting when the product actually contributes the chemical: exempt
 * chemicals, exempt products, and zero-concentration products never need one. Every screen and the report export
 * use this, so a qualifier is never pending in one place and resolved in another.
 */
export function chemicalEhsQualifierNeedsReview(candidate: TierIIChemicalCandidate): boolean {
  return ehsQualifierPendingProducts(candidate).length > 0;
}

export function ehsQualifierPendingProducts(
  candidate: TierIIChemicalCandidate,
): TierIIChemicalCandidate['products'][number][] {
  if (candidate.exemptionCode) return [];
  return candidate.products.filter(
    (product) => !product.exemptionCode && product.concentration > 0 && product.ehsQualifier?.status === 'needs_review',
  );
}

export function chemicalReportingNeedsReview(candidate: TierIIChemicalCandidate): boolean {
  return chemicalInventoryNeedsReview(candidate) || chemicalEhsQualifierNeedsReview(candidate);
}

/**
 * Reporting can't be avoided and no route is chosen yet: the user must pick chemical or mixture reporting.
 * Readiness blocks on this and Threshold Review asks for it, so they always agree on which chemicals need a choice.
 */
export function chemicalNeedsReportingDecision(candidate: TierIIChemicalCandidate): boolean {
  const outcome = candidate.reportingOutcome;
  return (
    outcome.reported &&
    outcome.source === 'unavoidable' &&
    outcome.basisSource === 'default' &&
    !chemicalReportingCoverage(candidate).fullyCovered
  );
}

/**
 * A product without constituents reaches its threshold but has no route yet: report it by name, add its
 * constituents, or exempt it. Below-threshold products and elected mixtures need no decision.
 */
export function productNeedsReportingDecision(product: TierIIProductReportingCandidate): boolean {
  return (
    product.kind === 'withoutConstituents' &&
    !product.exemptionCode &&
    !product.reportAsMixture &&
    product.maxAmountLbs != null &&
    product.maxAmountLbs >= product.thresholdLbs
  );
}
